import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Globe, Check, ChevronDown } from 'lucide-react';

const LanguageSelector = ({ 
  variant = 'default', // 'default', 'compact', 'navbar', 'footer'
  showLabel = true,
  className = '' 
}) => {
  const { currentLanguage, changeLanguage, supportedLanguages, isTranslating } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentLang = supportedLanguages.find(l => l.code === currentLanguage) || supportedLanguages[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    setIsOpen(false);
  };

  // Compact version for navbar
  if (variant === 'compact' || variant === 'navbar') {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1.5 px-2 py-1.5 text-sm text-white/85 hover:text-white hover:bg-white/10 rounded-md transition"
          data-testid="language-selector-compact"
          aria-label="Change language"
        >
          <span className="text-base">{currentLang.flag}</span>
          <span className="hidden sm:inline text-xs font-medium">{currentLang.code.toUpperCase()}</span>
          <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50">
            <div className="px-3 py-2 border-b border-gray-100">
              <p className="text-xs text-gray-500 font-medium">Select Language</p>
            </div>
            {supportedLanguages.map(lang => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-gray-50 transition ${
                  currentLanguage === lang.code ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                }`}
                data-testid={`lang-select-${lang.code}`}
              >
                <span className="flex items-center gap-2">
                  <span>{lang.flag}</span>
                  <span>{lang.nativeName}</span>
                </span>
                {currentLanguage === lang.code && <Check className="w-4 h-4" />}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Footer version
  if (variant === 'footer') {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 text-gray-400 hover:text-white text-sm transition"
          data-testid="language-selector-footer"
        >
          <Globe className="w-4 h-4" />
          <span>{currentLang.flag} {currentLang.nativeName}</span>
          <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute bottom-full mb-2 left-0 w-48 bg-gray-800 rounded-lg shadow-xl border border-gray-700 py-1 z-50">
            {supportedLanguages.map(lang => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-gray-700 transition ${
                  currentLanguage === lang.code ? 'bg-gray-700 text-white' : 'text-gray-300'
                }`}
              >
                <span className="flex items-center gap-2">
                  <span>{lang.flag}</span>
                  <span>{lang.nativeName}</span>
                </span>
                {currentLanguage === lang.code && <Check className="w-4 h-4" />}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Default full version
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isTranslating}
        className={`flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 transition ${
          isTranslating ? 'opacity-50 cursor-wait' : ''
        }`}
        data-testid="language-selector"
      >
        <Globe className="w-4 h-4 text-gray-500" />
        <span className="text-lg">{currentLang.flag}</span>
        {showLabel && <span className="text-sm font-medium text-gray-700">{currentLang.nativeName}</span>}
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50">
          <div className="px-4 py-2 border-b border-gray-100">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Select Language</p>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {supportedLanguages.map(lang => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`w-full px-4 py-2.5 text-left flex items-center justify-between hover:bg-gray-50 transition ${
                  currentLanguage === lang.code ? 'bg-blue-50' : ''
                }`}
                data-testid={`lang-option-${lang.code}`}
              >
                <span className="flex items-center gap-3">
                  <span className="text-xl">{lang.flag}</span>
                  <div>
                    <p className={`text-sm font-medium ${currentLanguage === lang.code ? 'text-blue-700' : 'text-gray-900'}`}>
                      {lang.nativeName}
                    </p>
                    <p className="text-xs text-gray-500">{lang.name}</p>
                  </div>
                </span>
                {currentLanguage === lang.code && (
                  <Check className="w-5 h-5 text-blue-600" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
